import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { Mic, Video, Target, Clock, Zap, MessageSquare, Shield, Play, Timer, PhoneCall, Handshake } from 'lucide-react';
import { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';

const RoleplayShowcase = () => {
  const { ref: sectionRef, isVisible } = useScrollAnimation({ threshold: 0.2 });
  const [activeMode, setActiveMode] = useState(0);

  // Auto-rotate through modes
  useEffect(() => {
    if (!isVisible) return;
    const interval = setInterval(() => {
      setActiveMode(prev => (prev + 1) % modes.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [isVisible]);

  const modes = [
    {
      id: 'roleplay',
      title: 'Role-Play',
      subtitle: 'Full Sales Conversations',
      description: 'Practice complete discovery-to-close conversations with AI buyers who push back on weak questions and reward strong SPIN technique.',
      color: 'primary',
      icon: MessageSquare,
      features: [
        { icon: Mic, text: 'Voice or text input modes' },
        { icon: Video, text: 'AI sees your expressions & gestures' },
        { icon: Target, text: 'Live SPIN & discovery tracking' },
        { icon: Clock, text: 'Timed 15-minute sessions' },
      ],
      mockup: (
        <div className="space-y-3">
          <div className="flex justify-end">
            <div className="bg-primary text-primary-foreground px-4 py-2 rounded-xl rounded-br-md text-sm max-w-[80%]">
              Can you tell me about your current packaging challenges?
            </div>
          </div>
          <div className="flex justify-start">
            <div className="bg-muted px-4 py-2 rounded-xl rounded-bl-md text-sm max-w-[80%] border border-border">
              We've had stacking issues. Damage runs 5-10% per affected batch.
            </div>
          </div>
          <div className="flex items-center gap-2 pt-2">
            <div className="px-2 py-1 rounded-lg bg-muted text-[10px] font-sans tabular-nums border border-border">
              <span className="text-foreground">S</span>
              <span className="text-muted-foreground ml-1">P</span>
              <span className="text-primary ml-1">I</span>
            </div>
            <div className="px-2 py-1 rounded-lg bg-muted text-[10px] font-sans tabular-nums text-muted-foreground border border-border">
              Discovery: <span className="text-primary font-bold">42%</span>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'speedsell',
      title: 'Speed-Sell',
      subtitle: '90-Second Elevator Pitch',
      description: 'Master the art of the quick pitch. Engage the buyer, deliver a compelling S.T.A.R.R. story, and close strong — all in 90 seconds.',
      color: 'primary',
      icon: Timer,
      features: [
        { icon: Video, text: 'Camera-enabled recording' },
        { icon: Play, text: 'Engage → Story → Close format' },
        { icon: Clock, text: '90-second countdown timer' },
        { icon: Target, text: 'S.T.A.R.R. story framework scoring' },
      ],
      mockup: (
        <div className="text-center space-y-4">
          <div className="w-20 h-20 mx-auto rounded-full bg-primary/10 flex items-center justify-center border border-primary/20">
            <span className="font-sans tabular-nums text-3xl font-bold text-primary">87</span>
          </div>
          <div className="text-sm text-muted-foreground">seconds remaining</div>
          <div className="flex justify-center gap-2">
            <div className="px-3 py-1.5 rounded-lg bg-status-success-bg text-status-success-fg text-[10px] font-bold uppercase tracking-wider">
              Engage
            </div>
            <div className="px-3 py-1.5 rounded-lg bg-primary/20 text-primary text-[10px] font-bold uppercase tracking-wider">
              S.T.A.R.R. Story
            </div>
            <div className="px-3 py-1.5 rounded-lg bg-muted text-muted-foreground text-[10px] font-bold uppercase tracking-wider border border-border">
              Close
            </div>
          </div>
          <div className="w-full h-1.5 rounded-full bg-muted overflow-hidden">
            <div className="h-full bg-primary rounded-full" style={{ width: '35%' }} />
          </div>
        </div>
      ),
    },
    {
      id: 'objections',
      title: 'Objection Drills',
      subtitle: 'Rapid-Fire Objection Handling',
      description: 'Master the Clarify-Address-Confirm framework with focused drills. Handle objections until you get explicit buyer acceptance.',
      color: 'primary',
      icon: Zap,
      features: [
        { icon: Zap, text: 'Rapid-fire objection practice' },
        { icon: Shield, text: '"Stay in the Ring" challenge mode' },
        { icon: Target, text: 'CAC framework scoring' },
        { icon: MessageSquare, text: 'Must confirm resolution to advance' },
      ],
      mockup: (
        <div className="space-y-3">
          <div className="flex justify-start">
            <div className="bg-muted px-4 py-2 rounded-xl rounded-bl-md text-sm max-w-[80%] border border-border">
              <span className="text-primary text-[10px] font-bold uppercase tracking-wider block mb-1">Objection #3</span>
              "Your pricing is 15% higher than our current supplier."
            </div>
          </div>
          <div className="flex justify-end">
            <div className="bg-primary/10 text-foreground px-4 py-2 rounded-xl rounded-br-md text-sm max-w-[80%] border border-primary/20">
              I hear that pricing is a concern. Can you help me understand what's driving that comparison?
            </div>
          </div>
          <div className="flex items-center gap-2 pt-2">
            <div className="px-2 py-1 rounded-lg bg-status-success-bg text-status-success-fg text-[10px] font-bold uppercase tracking-wider">Clarify</div>
            <div className="px-2 py-1 rounded-lg bg-primary/20 text-primary text-[10px] font-bold uppercase tracking-wider">Address</div>
            <div className="px-2 py-1 rounded-lg bg-muted text-muted-foreground text-[10px] font-bold uppercase tracking-wider border border-border">Confirm</div>
          </div>
        </div>
      ),
    },
    {
      id: 'coldcall',
      title: 'Cold Call',
      subtitle: '60-Second Opener',
      description: 'Earn the right to the next minute. Open strong, survive the brush-off, and land a clear next step before the clock runs out.',
      color: 'primary',
      icon: PhoneCall,
      features: [
        { icon: Clock, text: '60-second call window' },
        { icon: Shield, text: 'Realistic brush-offs and hang-ups' },
        { icon: Target, text: 'Opener, value, and ask scoring' },
        { icon: MessageSquare, text: 'Booked-meeting outcome tracking' },
      ],
      mockup: (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <PhoneCall className="w-3.5 h-3.5 text-primary" />
              Dialing VP of Operations
            </div>
            <span className="font-sans tabular-nums text-sm font-bold text-primary">0:41</span>
          </div>
          <div className="flex justify-start">
            <div className="bg-muted px-4 py-2 rounded-xl rounded-bl-md text-sm max-w-[85%] border border-border">
              "I'm in the middle of something — what is this about?"
            </div>
          </div>
          <div className="flex justify-end">
            <div className="bg-primary/10 text-foreground px-4 py-2 rounded-xl rounded-br-md text-sm max-w-[85%] border border-primary/20">
              Totally fair — 30 seconds and you can tell me to go away.
            </div>
          </div>
          <div className="flex items-center gap-2 pt-2">
            <div className="px-2 py-1 rounded-lg bg-status-success-bg text-status-success-fg text-[10px] font-bold uppercase tracking-wider">Opener</div>
            <div className="px-2 py-1 rounded-lg bg-primary/20 text-primary text-[10px] font-bold uppercase tracking-wider">Value</div>
            <div className="px-2 py-1 rounded-lg bg-muted text-muted-foreground text-[10px] font-bold uppercase tracking-wider border border-border">Ask</div>
          </div>
        </div>
      ),
    },
    {
      id: 'negotiation',
      title: 'Negotiation',
      subtitle: 'Price & Terms Pressure',
      description: 'Defend your price against procurement-style pressure. Trade concessions for value instead of discounting your way to a weak close.',
      color: 'primary',
      icon: Handshake,
      features: [
        { icon: Shield, text: 'Procurement-grade pushback' },
        { icon: Target, text: 'Concession vs. value trade scoring' },
        { icon: Zap, text: 'Discount discipline tracking' },
        { icon: MessageSquare, text: 'Terms, scope, and timeline levers' },
      ],
      mockup: (
        <div className="space-y-3">
          <div className="flex justify-start">
            <div className="bg-muted px-4 py-2 rounded-xl rounded-bl-md text-sm max-w-[85%] border border-border">
              <span className="text-primary text-[10px] font-bold uppercase tracking-wider block mb-1">Procurement</span>
              "Take 12% off and we can sign this week."
            </div>
          </div>
          <div className="flex justify-end">
            <div className="bg-primary/10 text-foreground px-4 py-2 rounded-xl rounded-br-md text-sm max-w-[85%] border border-primary/20">
              I can get closer on price with a 24-month term. Would that work?
            </div>
          </div>
          <div className="flex items-center gap-2 pt-2">
            <div className="px-2 py-1 rounded-lg bg-muted text-[10px] font-sans tabular-nums text-muted-foreground border border-border">
              Discount held: <span className="text-primary font-bold">4%</span>
            </div>
            <div className="px-2 py-1 rounded-lg bg-status-success-bg text-status-success-fg text-[10px] font-bold uppercase tracking-wider">Value traded</div>
          </div>
        </div>
      ),
    },
  ];

  return (
    <section ref={sectionRef} className="py-20 sm:py-28 relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6">
        {/* Section Header */}
        <div className={cn(
          "text-center max-w-3xl mx-auto mb-12 sm:mb-16 transition-all duration-700",
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
        )}>
          <span className="inline-flex items-center gap-2 mb-4 rounded-full border border-subtle bg-muted/40 px-3 py-1.5">
            <Zap className="w-3.5 h-3.5 text-primary" />
            <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-muted-foreground">Five ways to practice</span>
          </span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-[2.75rem] leading-[1.08] tracking-tight mb-3 text-foreground">
            Practice Like It's{' '}
            <span className="text-primary block sm:inline">Competition Day</span>
          </h2>
          <p className="text-muted-foreground text-lg">
            Master every aspect of sales with five focused practice modes
          </p>
        </div>

        {/* Mode Selector Tabs */}
        <div className={cn(
          "flex flex-wrap justify-center gap-2 sm:gap-3 mb-8 sm:mb-12 transition-all duration-700 delay-200",
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
        )}>
          {modes.map((mode, index) => {
            const Icon = mode.icon;
            return (
              <button
                key={mode.id}
                onClick={() => setActiveMode(index)}
                className={cn(
                  "px-4 sm:px-6 py-3 rounded-xl text-sm font-semibold transition-all duration-300 flex items-center gap-2 border",
                  activeMode === index
                    ? "bg-primary text-primary-foreground border-primary shadow-elevation-1"
                    : "bg-muted text-muted-foreground border-border hover:bg-secondary hover:text-foreground"
                )}
              >
                <Icon className="w-4 h-4" />
                <span className="hidden sm:inline">{mode.title}</span>
              </button>
            );
          })}
        </div>

        {/* Mode Cards */}
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left - Description */}
          <div className={cn(
            "transition-all duration-500",
            isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-12"
          )}>
            {modes.map((mode, index) => {
              const Icon = mode.icon;
              return (
                <div
                  key={mode.id}
                  className={cn(
                    "transition-all duration-500 space-y-6",
                    activeMode === index ? "opacity-100 block" : "opacity-0 hidden"
                  )}
                >
                  <div>
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-primary/10 text-primary text-xs font-bold uppercase tracking-wider mb-4 border border-primary/20">
                      <Icon className="w-4 h-4" />
                      {mode.subtitle}
                    </div>
                    <h3 className="font-display text-2xl sm:text-3xl font-bold text-foreground">
                      {mode.title}
                    </h3>
                  </div>
                  <p className="text-muted-foreground text-lg leading-relaxed">
                    {mode.description}
                  </p>
                  <div className="space-y-3">
                    {mode.features.map((feature, i) => (
                      <div 
                        key={i}
                        className="flex items-center gap-3 p-3 rounded-xl bg-card border border-border shadow-elevation-1 hover:bg-secondary transition-colors group"
                      >
                        <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-muted text-muted-foreground group-hover:text-primary transition-colors">
                          <feature.icon className="w-4 h-4" />
                        </div>
                        <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                          {feature.text}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Right - Mockup */}
          <div className={cn(
            "transition-all duration-500 delay-200",
            isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12"
          )}>
            {modes.map((mode, index) => (
              <div
                key={mode.id}
                className={cn(
                  "transition-all duration-500 relative",
                  activeMode === index ? "opacity-100 block" : "opacity-0 hidden"
                )}
              >
                {/* Mockup Container */}
                <div className="relative bg-card rounded-2xl overflow-hidden border border-border shadow-elevation-2 transition-all duration-300">
                  {/* Header */}
                  <div className="flex items-center justify-between px-4 py-3 bg-muted/50 border-b border-border">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center border border-primary/20">
                        <mode.icon className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <div className="font-semibold text-sm text-foreground">{mode.title}</div>
                        <div className="text-xs text-muted-foreground">{mode.subtitle}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-status-success-bg/20 text-status-success-fg text-[10px] font-bold uppercase tracking-wider">
                      <div className="w-1.5 h-1.5 rounded-full bg-status-success" />
                      Live
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-5 sm:p-6 bg-card min-h-[200px]">
                    {mode.mockup}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default RoleplayShowcase;
